import { useCallback, useEffect, useMemo, useState } from "react";
import { useCustomMeasurements } from "../classes/contexts/CustomMeasurementsContext";
import { useRemoteRenderContext } from "../classes/contexts/RemoteRenderContext";
import {
  TOOL_TO_CATEGORY_MAP,
  TOOL_TO_PYTHON_CLASS_MAP,
} from "../classes/enums/ToolsEnum";
import {
  addCustomMeasurementToDatabase,
  deleteCustomMeasurementFromDatabase,
  updateCustomMeasurementInDatabase,
} from "../services/CustomMeasurementService";
import { useRouteState } from "./useRouteState";

export const useSppActions = (
  areViewsReady,
  refreshData,
  currentPlanId,
  comparedPlanId = null
) => {
  const { client: renderClient } = useRemoteRenderContext();
  const { routeType, params } = useRouteState();
  const { customMeasurements, removeCustomMeasurement } =
    useCustomMeasurements();

  const [activeParameters, setActiveParameters] = useState({});
  const currentPlanIdInternal = currentPlanId;
  const planIds = useMemo(() => {
    const ids = [];

    if (currentPlanIdInternal) {
      ids.push(currentPlanIdInternal);
    }

    if (comparedPlanId) {
      ids.push(comparedPlanId);
    }

    return Array.from(new Set(ids));
  }, [currentPlanIdInternal, comparedPlanId]);
  const primaryPlanId = planIds[0] || null;

  const normalizeVisibilityResponse = useCallback(response => {
    try {
      const parsed =
        typeof response === "string" ? JSON.parse(response) : response;

      if (!parsed || parsed.success === false) {
        return [];
      }

      return parsed.visibleMeasurements || [];
    } catch (error) {
      return [];
    }
  }, []);

  // Sync visibility state function
  const syncVisibilityState = useCallback(
    async viewsReady => {
      if (
        !renderClient ||
        !routeType ||
        !viewsReady ||
        !primaryPlanId
      ) {
        return;
      }

      try {
        const step = routeType.toLowerCase();
        const response = await renderClient.getVisibleMeasurements(
          primaryPlanId,
          step
        );

        if (response) {
          const parsedResponse =
            typeof response === "string" ? JSON.parse(response) : response;

          if (parsedResponse.success) {
            const newActiveParameters = {};

            // Process regular measurements from backend
            parsedResponse.visibleMeasurements?.forEach(measurement => {
              newActiveParameters[measurement.measurementId] = true;
            });

            // Process ALL custom measurements (both visible and invisible)
            Object.keys(customMeasurements).forEach(measurementId => {
              // If not already set by backend response, default to false
              if (!(measurementId in newActiveParameters)) {
                newActiveParameters[measurementId] = false;
              }
            });

            setActiveParameters(newActiveParameters);
          }
        }
      } catch (error) {}
    },
    [routeType, renderClient, customMeasurements, primaryPlanId]
  );

  const syncComparedPlanSppVisibility = useCallback(async () => {
    if (
      !renderClient ||
      !routeType ||
      !currentPlanIdInternal ||
      !comparedPlanId
    ) {
      return;
    }

    const step = routeType.toLowerCase();

    try {
      const [currentResponse, comparedResponse] = await Promise.all([
        renderClient.getVisibleMeasurements(currentPlanIdInternal, step),
        renderClient.getVisibleMeasurements(comparedPlanId, step),
      ]);

      const currentVisible = normalizeVisibilityResponse(currentResponse);
      const comparedVisible = normalizeVisibilityResponse(comparedResponse);

      const currentIds = new Set(
        currentVisible.map(measurement => measurement.measurementId)
      );
      const comparedIds = new Set(
        comparedVisible.map(measurement => measurement.measurementId)
      );

      const measurementsToToggle = new Set();

      currentIds.forEach(measurementId => {
        if (!comparedIds.has(measurementId)) {
          measurementsToToggle.add(measurementId);
        }
      });

      comparedIds.forEach(measurementId => {
        if (!currentIds.has(measurementId)) {
          measurementsToToggle.add(measurementId);
        }
      });

      for (const measurementId of measurementsToToggle) {
        const isCustomMeasurement = !!customMeasurements[measurementId];
        if (isCustomMeasurement) {
          await renderClient.setCustomMeasurementVisibility(
            comparedPlanId,
            measurementId,
            step
          );
        } else {
          await renderClient.setMeasurementVisibility(
            comparedPlanId,
            measurementId,
            step
          );
        }
      }

      await syncVisibilityState(areViewsReady);
    } catch (error) {
      console.error(
        "[useSppActions] Failed to sync compared plan SPP visibility",
        error
      );
    }
  }, [
    renderClient,
    routeType,
    currentPlanIdInternal,
    comparedPlanId,
    customMeasurements,
    syncVisibilityState,
    areViewsReady,
    normalizeVisibilityResponse,
  ]);

  // Toggle parameter visibility
  const toggleParameterVisibility = useCallback(
    async (param, isCustom = false) => {
      if (planIds.length === 0) {
        return;
      }

      const step = routeType.toLowerCase();

      const toggleForPlan = async planId => {
        if (isCustom) {
          // For custom measurements, we need to handle both simple and advanced types
          const response = await renderClient.setCustomMeasurementVisibility(
            planId,
            param.key,
            step
          );

          if (response && typeof response === "string") {
            try {
              const parsedResponse = JSON.parse(response);
              if (parsedResponse.error) {
              }
            } catch (e) {}
          }
        } else {
          await renderClient.setMeasurementVisibility(planId, param.key, step);
        }
      };

      try {
        for (const planId of planIds) {
          await toggleForPlan(planId);
        }

        await syncVisibilityState(areViewsReady);
      } catch (error) {
        try {
          await syncVisibilityState(areViewsReady);
        } catch (syncError) {}
      }
    },
    [
      renderClient,
      routeType,
      areViewsReady,
      syncVisibilityState,
      planIds,
    ]
  );

  // Wrapper functions for external use
  const onSppSelected = useCallback(
    param => toggleParameterVisibility(param, false),
    [toggleParameterVisibility]
  );

  const onSppSelectedCustom = useCallback(
    param => toggleParameterVisibility(param, true),
    [toggleParameterVisibility]
  );

  // Build measurement config for database
  const buildMeasurementConfig = useCallback(
    (tool, pythonClass, category, viewName) => {
      return {
        pythonClass,
        category,
        construction_tool: tool,
        digital_twin_id: params.twinId,
        viewtypes: [viewName],
        color: [],
        support: [],
      };
    },
    [params.twinId]
  );

  // Add custom measurement - database first, then Python backend
  const addCustomMeasurement = useCallback(
    async (tool, viewName, mousePosition = null) => {
      // Add mousePosition parameter
      if (!renderClient) {
        return;
      }

      if (!primaryPlanId) {
        return;
      }

      try {
        const pythonClass = TOOL_TO_PYTHON_CLASS_MAP[tool];
        const category = TOOL_TO_CATEGORY_MAP[tool];

        if (!pythonClass || !category) {
          return;
        }

        // 1. Create in database first to get real unique_identifier
        const measurementConfig = buildMeasurementConfig(
          tool,
          pythonClass,
          category,
          viewName
        );

        const dbResponse = await addCustomMeasurementToDatabase(
          measurementConfig
        );

        const serializedResponse = JSON.parse(dbResponse);

        if (
          !serializedResponse.success ||
          !serializedResponse.unique_identifiers ||
          serializedResponse.unique_identifiers.length === 0
        ) {
          throw new Error("Failed to get unique identifier from database");
        }

        const realUniqueId = serializedResponse.unique_identifiers[0];
        const dbId = serializedResponse.measurement_ids[0];
        // Log mouse position if provided
        if (mousePosition) {
        }

        // 2. Call Python backend with the real unique_identifier and mouse position
        await renderClient.addCustomMeasurement(
          primaryPlanId,
          viewName,
          pythonClass,
          category,
          tool,
          realUniqueId,
          dbId,
          mousePosition
        );

        await syncVisibilityState(areViewsReady);
      } catch (error) {
        // Handle specific error cases
        if (
          error.message &&
          error.message.includes(
            "'NoneType' object has no attribute 'filter_support_by_tag'"
          )
        ) {
          alert(
            "Unable to create measurement: Vertebra selection tool is not configured. Please contact support."
          );
        } else if (error.message && error.message.includes("Tool config")) {
          alert(
            "Unable to create measurement: Tool configuration error. Please try again or contact support."
          );
        } else {
        }
      }
    },
    [
      renderClient,
      areViewsReady,
      syncVisibilityState,
      buildMeasurementConfig,
      primaryPlanId,
    ]
  );

  // Remove custom measurement - Python backend first, then database
  const removeCustomParameter = useCallback(
    async param => {
      if (!primaryPlanId) {
        return;
      }

      const dbId = param._id || param.config?._id;
      const measurementId = param.measurementId || param.key;
      if (!dbId) {
        return;
      }

      if (!measurementId) {
        return;
      }

      try {
        // 1. Try to remove from Python backend first (might fail if not instantiated)
        try {
          await renderClient.removeCustomMeasurement(
            primaryPlanId,
            measurementId
          );
        } catch (backendError) {
          // Don't throw - continue with database and frontend cleanup
        }

        // 2. Remove from database using the database ID
        try {
          await deleteCustomMeasurementFromDatabase(dbId);
        } catch (dbError) {
          // This is more critical - throw if database removal fails
          throw dbError;
        }

        // 3. Remove from local context (always do this to clean up frontend state)
        removeCustomMeasurement(measurementId);
        // 4. Refresh configs after removal
        if (refreshData) {
          refreshData();
        }

        // 5. Sync visibility state to ensure UI is consistent
        await syncVisibilityState(areViewsReady);
      } catch (error) {
        // Even if there was an error, try to sync the state
        try {
          await syncVisibilityState(areViewsReady);
        } catch (syncError) {}

        // Don't re-throw - we want to handle errors gracefully
      }
    },
    [
      renderClient,
      removeCustomMeasurement,
      syncVisibilityState,
      areViewsReady,
      refreshData,
      primaryPlanId,
    ]
  );

  // Listen for measurement changes and update database
  useEffect(() => {
    // Update database when measurements change
    const updateDatabaseFromChanges = async () => {
      const measurementIds = Object.keys(customMeasurements);
      let updatedCount = 0;

      for (const measurementId of measurementIds) {
        const measurement = customMeasurements[measurementId];
        if (measurement.config && measurement.value !== undefined) {
          try {
            const { _id, ...configWithoutId } = measurement.config;
            const updatedConfig = {
              ...configWithoutId,
              value: measurement.value,
              support: measurement.support,
            };

            await updateCustomMeasurementInDatabase(
              updatedConfig,
              measurement.config._id
            );

            updatedCount++;
          } catch (error) {}
        }
      }

      // ✅ NOW refresh configs after all database updates are done
      if (updatedCount > 0 && refreshData) {
        refreshData();
      }
    };

    // Only run if we have measurements with data
    const hasUpdatableMeasurements = Object.values(customMeasurements).some(
      measurement => measurement.config && measurement.value !== undefined
    );

    if (hasUpdatableMeasurements) {
      // Debounce the database updates
      const timeoutId = setTimeout(updateDatabaseFromChanges, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [customMeasurements, refreshData]);

  // Initial sync when component mounts
  useEffect(() => {
    syncVisibilityState(areViewsReady);
  }, [syncVisibilityState, areViewsReady]);

  return {
    onSppSelected,
    onSppSelectedCustom,
    activeParameters,
    addCustomMeasurement,
    removeCustomParameter,
    syncVisibilityState,
    syncComparedPlanSppVisibility,
  };
};

export default useSppActions;
